import getUserFullname from './getUserFullname';
import getUserDisplayName from './getUserDisplayName';
import getUserStatusText from './getUserStatusText';
import setUserWages from './setUserWages';
import utils from '../utils';
import context from '../context';
import { USER_STATUSES } from '../constants';
import canActOnLocation from '../helpers/canActOnLocation';
import gettext from '../lang/gettext';

export default function extendUserModel(
  model,
  { mutate = false, calendarDisplayOptimize = false, minimalExtend = false, skipWages = false } = {}
) {
  let user = model;

  if (
    (calendarDisplayOptimize || typeof user.avatar === 'undefined') &&
    context.personasDict[user.id]
  ) {
    if (mutate) {
      user = context.personasDict[user.id];
    } else {
      Object.assign(user, context.personasDict[user.id]);
    }
    return user;
  }

  // temporary hack until server marshalls it as null in the first place
  if (
    user.avatar &&
    user.avatar.startsWith(
      'https://res.cloudinary.com/gangverk/image/upload/b_rgb:b6b6b6/l_text:Verdana_45_bold:'
    )
  ) {
    user.avatar = null;
  }

  if (user.avatar && user.avatar.startsWith('https://www.gravatar.com/avatar')) {
    user.avatar = user.avatar.replace(/\?d=.*$/, '?d=404');
  }

  if (!('deleted' in user)) {
    user.deleted = false;
  }

  if (user.deleted && (!user.name || !user.name.length)) {
    user.name = gettext('Deleted user');
  }

  if (!user.name || !user.name.length) {
    user.name = `[${gettext('unknown')}]`;
  }

  user.fullLegalName = getUserFullname(context.showLastNameFirst, user);
  user.fullname = getUserFullname(context.showLastNameFirst, user, true);

  if (!user.role) {
    user.role = 'user';
  }

  switch (user.role) {
    case 'admin':
      user.isAdmin = true;
      user.isManager = true;
      break;
    case 'manager':
      user.isAdmin = false;
      user.isManager = true;
      break;
    default:
      user.isAdmin = user.isManager = false;
  }

  user.isMe = context.me && context.me.id === user.id;
  user.type = 'user';

  if (!user.isAuthenticationDotUser) {
    if (user.status !== USER_STATUSES.REQUESTED_INVITE && !context.personasDict[user.id]) {
      user.deleted = true;
    }

    const relationships = context.getUserGroups(user);

    user.groupIds = relationships.map((g) => g.id);
    user.groups = relationships.filter((g) => g.type === 'group');
    user.positions = relationships.filter((g) => g.type === 'position');
    user.locations = relationships.filter((g) => g.type === 'location');
    user.locationIds = user.locations.map((g) => g.id);
    user.positionIds = user.positions.map((g) => g.id);

    if (!user.managers) {
      user.managers = [];
    }

    user.managers = user.managers.map(
      (u) => context.personasDict[u.id] || { name: `[${gettext('unknown')}]` }
    );

    if (!minimalExtend) {
      user.position = user.positions.map((g) => g.name).join(', ');
      user.position50 = user.position
        ? `${user.position.substr(0, 50)}${user.position.length > 50 ? '...' : ''}`
        : '';
      user.location = user.locations.map((g) => g.name).join(', ');
      user.location50 = user.location
        ? `${user.location.substr(0, 50)}${user.location.length > 50 ? '...' : ''}`
        : '';
    }

    if (user.positions && user.positions.length && user.positions[0].color) {
      user.color = user.positions[0].color;
    }

    if (!('hiddenOnSchedule' in user)) {
      Object.defineProperty(user, 'hiddenOnSchedule', {
        enumerable: true,
        get() {
          return Boolean(context.personasHiddenOnScheduleDict[user.id]);
        },
        set(value) {
          context.personasHiddenOnScheduleDict[user.id] = value;
        },
      });
    }
  }

  if (user.personasHiddenOnSchedule) {
    user.personasHiddenOnScheduleDict = user.personasHiddenOnSchedule.reduce(
      (acc, p) => ({
        [p.id]: true,
        ...acc,
      }),
      {}
    );
  }

  user.canActOnLocation = (location) => canActOnLocation(user, location);
  user.getDisplayName = () => getUserDisplayName(user);
  user.normalizedDisplayName = utils.normalizeString(user.fullname);

  user.searchValue = `${user.fullname} ${user.email} ${user.position} ${user.location}`;

  user.order = user.fullname;

  if (user.birthdayDate && typeof user.birthdayDate === 'string') {
    user.birthdayDate = utils.getRealDate(user.birthdayDate);
  }

  if (user.hireDate && typeof user.hireDate === 'string') {
    user.hireDate = utils.getRealDate(user.hireDate);
  }

  if (user.isMe) {
    user.order = 1;
  }

  if (user.deactivatedAt) {
    user.deactivatedAt = new Date(user.deactivatedAt);
  }

  user.missingInviteDetails = !user.email && !user.phone;
  user.statusText = getUserStatusText(user);

  // If the `user` object happens to be non-extensible then creating new properties on the object
  // will not work in `setUserWages`
  if (!skipWages && Object.isExtensible(user)) {
    setUserWages(user, context.personasDict);
  }

  if (!user.hoursCap) {
    user.hoursCap = null;
  }

  user.isUnverified = user.pending || user.status === USER_STATUSES.UNVERIFIED;

  return user;
}
